import React, { FC, useState, PropsWithChildren } from 'react';

import { Box, Image, Heading, Text, Icon, Button } from '@lucid/components-react';
import kebabCase from 'lodash/kebabCase';
import { Colors } from '@lucid/theme';

import { LucidTheme } from '../../types/types';

export type PersonColor = Exclude<Colors.VariantsType, 'white' | 'yellow' | 'lime'>;

export type PersonData = {
  id: number;
  fieldName: string;
  fieldImageUrl: string;
  fieldTitle: string;
  fieldCompanyName: string;
  fieldBio: string;
};

export interface PersonProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets which people should be displayed */
  person: PersonData;
  /** Sets the color scheme for the people backgrounds */
  backgroundColor?: PersonColor;
}

const Person: FC<PropsWithChildren<PersonProps>> = ({ className, person, backgroundColor = 'blue' }) => {
  const { id, fieldName, fieldImageUrl, fieldTitle, fieldCompanyName, fieldBio } = person;
  const bioElementId = fieldName ? `${kebabCase(fieldName.toLowerCase())}-${id}` : `person-${id}`;
  const [isExpanded, setIsExpanded] = useState(false);
  const handleOnClick = () => setIsExpanded(!isExpanded);
  return (
    <Box
      css={{
        margin: '0 auto',
        maxWidth: '350px',
        display: 'flex',
        flexDirection: 'column',
      }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      <Image
        css={(theme: LucidTheme) => ({
          aspectRatio: '1 / 1',
          backgroundColor: theme.colors[backgroundColor][10],
          borderRadius: '50%',
          overflow: 'hidden',
          boxShadow: `inset 0px 0px 0px 10px ${
            theme.colors[backgroundColor][backgroundColor === 'coolGrey' ? 100 : 60]
          }`,
          marginBottom: theme.space.s24,
          marginLeft: 'auto',
          marginRight: 'auto',
          width: '75%',
          imageRendering: '-webkit-optimize-contrast',
        })}
        imageAlt={`${fieldName} headshot`}
        imageSrc={fieldImageUrl!}
        loading="lazy"
      />
      {fieldBio ? (
        <Button
          aria-controls={bioElementId}
          aria-expanded={isExpanded}
          onClick={handleOnClick}
          tabIndex={0}
          variant="basic"
        >
          <Heading as="h3" css={{ display: 'flex', justifyContent: 'space-between' }} variant="headlineL">
            {fieldName}
            <Icon
              color="coolGrey.90"
              css={(theme: LucidTheme) => ({
                transition: 'transform 0.3s',
                transformOrigin: 'center',
                transform: isExpanded ? 'scaleY(-1)' : 'scaleY(1)',
                width: theme.space.s16,
              })}
              type="CarotDown"
            />
          </Heading>
        </Button>
      ) : (
        <Heading as="h3" variant="headlineL">
          {fieldName}
        </Heading>
      )}
      {fieldTitle && <Text variant="paragraphM300">{fieldTitle}</Text>}
      {fieldCompanyName && <Text variant="paragraphS300">{fieldCompanyName}</Text>}
      {fieldBio && (
        <Text
          aria-hidden={!isExpanded}
          css={{
            maxHeight: isExpanded ? '1000px' : 0,
            overflow: 'hidden',
            transition: isExpanded ? 'max-height 1.2s' : 'max-height 0.5s',
          }}
          id={bioElementId}
          variant="paragraphS300"
        >
          {fieldBio}
        </Text>
      )}
    </Box>
  );
};

export { Person };
export default Person;
